import React, { Suspense } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import './services/axiosConfig';
import {
    IonApp,
    IonRouterOutlet,
    IonMenu,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonLabel,
    IonMenuToggle,
    setupIonicReact,
    IonButtons,
    IonButton,
    IonAvatar,
    IonImg,
    IonFooter,
    IonSpinner
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { menuOutline, personCircle } from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';

import CustomMenu from './components/CustomMenu';

/* Theme variables */
import './theme/variables.css';
import './theme/custom.css';

/* Ionic CSS */
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Pages - Lazy loaded */
const Jobs = React.lazy(() => import('./pages/Jobs'));
const Login = React.lazy(() => import('./pages/Login'));
const Home = React.lazy(() => import('./pages/Home'));
const Profit = React.lazy(() => import('./pages/Profit'));

/* Context */
import { ConfigProvider } from './contexts/ConfigContext';
import { MenuProvider } from './contexts/MenuContext';

// Constants
const isNative = Capacitor.isNativePlatform();
const BASE_PATH = isNative ? '' : '/panel';

// Loading component for suspense fallback
const LoadingSpinner: React.FC = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
    }}>
        <IonSpinner />
    </div>
);

setupIonicReact();

// Authentication check function
const isAuthenticated = () => !!localStorage.getItem('jwtToken');

// Import menu.css when authenticated
if (isAuthenticated()) {
    import('./theme/custom-menu.css');
}

// Protected Route component
const ProtectedRoute: React.FC<{
    component: React.ComponentType<any>;
    path: string;
    exact?: boolean;
}> = ({ component: Component, ...rest }) => {
    return (
        <Route
            {...rest}
            render={props =>
                isAuthenticated() ? (
                    <Component {...props} />
                ) : (
                    <Redirect to={`${BASE_PATH}/login`} />
                )
            }
        />
    );
};

// Title Component to handle dynamic page titles
const DynamicPageTitle: React.FC = () => {
    const location = useLocation();

    const getPageTitle = () => {
        const path = location.pathname.replace(BASE_PATH, '');
        switch(path) {
            case '/home': return 'Home';
            case '/jobs': return 'Jobs';
            case '/profit': return 'Profit Management';
            case '/login': return 'Login';
            default: return 'ERP System';
        }
    };

    return (
        <IonTitle className="ion-text-center">
            {getPageTitle()}
        </IonTitle>
    );
};

const App: React.FC = () => {
    const [isMenuCollapsed, setIsMenuCollapsed] = React.useState(window.innerWidth < 992);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMenuCollapsed(window.innerWidth < 992);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <IonApp>
            <ConfigProvider>
                <MenuProvider>
                    <IonReactRouter basename={BASE_PATH}>
                        {isAuthenticated() && (
                            <>
                                <CustomMenu
                                    isCollapsed={isMenuCollapsed}
                                    onToggle={() => setIsMenuCollapsed(!isMenuCollapsed)}
                                />

                                <IonHeader id="main-header">
                                    <IonToolbar>
                                        <IonButtons slot="start">
                                            <IonButton onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}>
                                                <IonIcon icon={menuOutline} />
                                            </IonButton>
                                        </IonButtons>
                                        <DynamicPageTitle />
                                        <IonButtons slot="end">
                                            <IonButton>
                                                
                                            </IonButton>
                                        </IonButtons>
                                    </IonToolbar>
                                </IonHeader>
                            </>
                        )}

                        <IonRouterOutlet id="main">
                            <Suspense fallback={<LoadingSpinner />}>
                                <Switch>
                                    <Route path="/login" component={Login} exact />
                                    <ProtectedRoute path="/jobs" component={Jobs} exact />
                                    <ProtectedRoute path="/home" component={Home} exact />
                                    <ProtectedRoute path="/profit" component={Profit} exact />
                                    <Redirect exact from="/" to="/home" />
                                </Switch>
                            </Suspense>
                        </IonRouterOutlet>
                    </IonReactRouter>
                </MenuProvider>
            </ConfigProvider>
        </IonApp>
    );
};

export default App;
